// src/Magic/index.js

import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { observer, inject } from 'mobx-react'
import { Helmet } from 'react-helmet'
import slugify from 'slugify'

import { message } from 'antd'

import Layout from 'components/Layout'
import FAQ from 'components/FAQ'
import Loading from 'components/Loading'
import { ucwords, defaultMetatags, getOfficeAddress, discectError } from 'lib/utils'
import events from 'lib/analytics'

// Google SEO:
// - https://schema.org/docs/full.html
// - https://developers.google.com/search/docs/guides/get-started
// - https://developers.google.com/search/docs/data-types/local-business
// - https://search.google.com/structured-data/testing-tool/m/0/
// - https://support.google.com/webmasters/answer/9012289

const Magic = observer(({ bondsmanStore }) => {
  const [isLoading, setIsLoading] = useState(0)
  const [tabId, setTabId] = useState(null)
  const [info, setInfo] = useState(null)
  const [currentLocation, setCurrentLocation] = useState(null)
  const [currentOffice, setCurrentOffice] = useState(null)
  const [primaryOffice, setPrimaryOffice] = useState(null)

  const { slug, location, county, officeSlug } = useParams()

  const navigate = useNavigate()
  
  const [messageApi, contextHolder] = message.useMessage()

  const _setIsLoading = useCallback((val) => {
    setIsLoading(v => val ? v + 1 : v - 1)
  }, [])

  const _setError = useCallback((error) => {
    messageApi.open({
      type: 'error',
      content: error
    })
  }, [messageApi])

  useEffect(() => {
    const _fetch = async () => {
      _setIsLoading(true)
      try {
        localStorage.setItem('magicLink', slug)
        localStorage.removeItem('magicLinkEmployee')

        const results = await bondsmanStore.fetchBondsmanByName(slug)
        if (results) {

          let currentOffice = null
          let primaryOffice = null
          if (results.bondsman?.offices) {
            if (officeSlug) {
              currentOffice = (results.bondsman?.offices || []).find(itm => itm.slug === officeSlug)
            }
            primaryOffice = (results.bondsman?.offices || []).find(itm => itm.isPrimary)
          } else if (results.bondsman?.office) {
            primaryOffice = { ...results.bondsman.office }
          }


          const info = results.bondsman

          localStorage.removeItem('magicLinkEmployee')
          localStorage.setItem('magicLinkAgency', JSON.stringify({ id: info.id, slug, companyName: info.name, isAgency: info.isAgency, office: currentOffice?.id || null }))
          events.magicPageView(info.companyName)
          let currentLocation = null
          if (location && county) {
            currentLocation = info.locations.find(itm => (slugify(itm.name, '_').toLowerCase() === county))
          }
          
          setCurrentLocation(currentLocation)
          setCurrentOffice(currentOffice)
          setPrimaryOffice(primaryOffice)
          setInfo(info)
        }
      } catch(e) {
        console.error(e)
        _setError(discectError(e))
      }
      _setIsLoading(false)
    }
    if (!info) _fetch()
  }, [_setIsLoading, _setError, bondsmanStore, county, location, info, officeSlug, slug])

  const _gotoSite = () => {
    if (currentLocation) {
      navigate(`/register/${location}-${county}-${currentLocation.id}`)
    } else {
      navigate(`/register`)
    }
  }

  const getSchema = () => {
    const name = `${info.landingPage.name} on Simply Bail` // Add City here
    const businessName = `${info.landingPage.name} | Simply Bail`
    const description = info.landingPage.description
    
    return (
      <script type="application/ld+json">{`{
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "WebSite",
            "@id": "https://simplybail.com/m/${slug}#website",
            "url": "https://simplybail.com/m/${slug}",
            "name":"${name}",
            "inLanguage": "en-US",
            "description":"${businessName}",
            "potentialAction": [
              {
                "@type": "SearchAction",
                "target": "https://simplybail.com/m/${slug}/search?s={search_term_string}",
                "query-input": "required name=search_term_string"
              }
            ]
          },
          {
            "@type": "ImageObject",
            "@id": "", // Logo location: https://gabailbonding.com/#primaryimage
            "inLanguage": "en-US",
            "url": "" // Logo file
          },
          {
            "@type":"WebPage",
            "@id": "https://simplybail.com/m/${slug}#webpage}",
            "url": "https://simplybail.com/m/${slug}",
            "name": "${businessName}",
            "isPartOf": {
              "@id": "https://simplybail.com/m/${slug}#website"
            },
            "inLanguage":"en-US",
            "primaryImageOfPage": {
              "@id": "" // Logo location: https://gabailbonding.com/#primaryimage
            },
            "datePublished": "2017-11-16T20:36:13+00:00",
            "dateModified": "2018-01-18T15:45:11+00:00",
            "description": "${description}",
            "potentialAction":[
              {
                "@type":"ReadAction",
                "target":[
                  "https://simplybail.com/m/${slug}"
                ]
              }
            ]
          }
        ]
      }`}</script>
    )
  }

  const _getReviews = () => {
    return (
      <div className="reviews__container">
        <header className="reviews__header">
          <p>{info.name} has received 4.7 / 5 Stars</p>
        </header>
        <h4>Recent Reviews</h4>
        <div className="reviews">
          <div className="review">
            <header>
              <div className="review__stars">
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fal fa-star"></i>
              </div>
              <div>
                
              </div>
            </header>
            <div className="review__content">
              <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed dictum maximus aliquet. Fusce bibendum condimentum urna, at mollis tortor eleifend aliquet. Phasellus iaculis rutrum mauris eu fringilla.</p>
            </div>
            <footer>
              <p>Bond amont was <strong>less than $500</strong> and was handled in <strong>18 hours and 32 minutes</strong>.</p>
            </footer>
          </div>
        </div>
      </div>
    )
  }

  // const _getCounties = () => {
  //   const { slug, info } = this.state

  //   if (info) {
  //     return info.locations.map((location, key) => {
  //       const extraStart = (key === (info.locations.length - 1)) ? 'and ' : null
  //       const extraEnd = (key < (info.locations.length - 1)) ? ', ' : null
  //       const stateSlug = slugify(location.state, '_').toLowerCase()
  //       const countySlug = slugify(location.name, '_').toLowerCase()
  //       const link = (
  //         <Fragment key={key}>
  //           {extraStart}
  //           <a href={`/m/${slug}/${stateSlug}/${countySlug}`}>{location.name} County</a>
  //           {extraEnd}
  //         </Fragment>
  //       )
  //       return link
  //     })
  //   }
  //   return null
  // }

  const _getMetaTags = () => {
    if (info) {
      let title = info.landingPage.name
      if (currentLocation) {
        title = `${title} of ${currentLocation.name} County | Simply Bail`
      }

      return {
        title,
        description: info.landingPage.description,
        url: `${window.location.protocol}//${window.location.host}${window.location.pathname}`
      }
    }
    return {
      title: '',
      description: '',
      url: `${window.location.protocol}//${window.location.host}${window.location.pathname}`
    }
  }

  const _getAddress = () => {
    let officeAddress = getOfficeAddress(primaryOffice)
    if (currentOffice) {
      officeAddress = getOfficeAddress(currentOffice)
    }
    return (
      <div className="companyProfile__address">
        <p>{currentOffice?.location || ''}</p>
        <p>{officeAddress?.mainText}</p>
        <p>{officeAddress?.secondaryText}</p>
        <p>{officeAddress?.phone}</p>
        <p>{officeAddress?.email}</p>
      </div>
    )
  }

  if (isLoading) {
    return <Loading test="Loading" />
  } else if (info) {
    let classes = 'companyProfile'
    classes = `${classes} companyProfile--${slug}`
    return (
      <Layout bodyClassName="layout--magic">
        {contextHolder}
        <div className={classes}>
          {defaultMetatags(_getMetaTags())}
          <Helmet>
            {getSchema()}
          </Helmet>
          <>
            <header className="companyProfile__header">
              <div className="companyProfile__header__logo">
                {info.landingPage.logo && <img src={info.landingPage.logo} alt={info.landingPage.name} />}
                {!info.landingPage.logo && <p>&nbsp;</p>}
              </div>
              <div className="companyProfile__header__info">
                <h1>{info.landingPage.name}</h1>
                {currentLocation && (
                  <p className="companyProfile__location">of {currentLocation.name} County, {ucwords(currentLocation.state.toLowerCase())}</p>
                )}
                {_getAddress()}
              </div>
            </header>
            <div className="companyProfile__content">
              {info.landingPage.description}
              {/* <p>Servicing {this._getCounties()}</p> */}
              <p style={{ display: 'none' }}>Average time till Bail Posted: <strong>16 hours and 15 minutes</strong></p>
            </div>
            <div className="companyProfile__actions">
              <button className="butt butt--teal" onClick={_gotoSite}>Click here to get someone out of jail</button>
              {info.url && <a href={info.url} alt={info.name}>Goto Website</a>}
            </div>
            <div className="tabs" style={{ display: 'none' }}>
              <div className="tabs__tabs">
                <button className={(tabId === 1) ? 'active' : ''} onClick={() => setTabId(1)}>FAQ</button>
                <button className={(tabId === 2) ? 'active' : ''} onClick={() => setTabId(2)}>Reviews (26)</button>
              </div>
            </div>
            <div className="tabs_sections">
              <div className={`files tabs_sections__section ${(tabId === 1) ? 'tabs_sections__section--active' : ''}`}>
                <FAQ />
              </div>
              <div className={`uploads tabs_sections__section ${(tabId === 2) ? 'tabs_sections__section--active' : ''}`}>
                {_getReviews()}
              </div>
            </div>
          </>
        </div>
      </Layout>
    )
  }
})

export default inject('bondsmanStore')(Magic)